import type {Property} from 'csstype';
import DOMPurify from 'dompurify';

import {sanitizeHtml} from '../../utils/markdownUtils';
import type {BackgroundType} from '../types';
import {BackgroundTypes} from '../types';

export const TEXT_OBJECT_PADDING = 6.9;
export const TEXT_OBJECT_PREVIEW_PADDING = 1.5;
// Maximum width for text objects in canvas grid units (7/9 of canvas width, leaving 2/9 for margin on the canvas)
const TEXT_OBJECT_MAX_WIDTH_UNITS = 7;

type TextStyles = {
  fontSize?: string;
  fontFamily?: string;
  maxWidth?: string;
  textAlign?: Property.TextAlign;
};

export type Rect = {
  width: number;
  height: number;
  top: number;
  left: number;
};

function setupTextMeasurementContainers(
  text: string,
  style: TextStyles,
  textScaleFactor: number,
  isPreviewMode: boolean
) {
  const globalFont = getComputedStyle(
    document.documentElement
  ).getPropertyValue('--global-font');

  const textPadding = isPreviewMode
    ? TEXT_OBJECT_PREVIEW_PADDING
    : TEXT_OBJECT_PADDING;

  const maxRectWidth = `${TEXT_OBJECT_MAX_WIDTH_UNITS * textScaleFactor}px`;

  const {
    fontSize = `${0.5 * textScaleFactor}px`,
    fontFamily = globalFont || 'Commissioner',
    maxWidth = maxRectWidth,
    textAlign = 'left',
  } = style;

  const textContainer = document.createElement('div');
  const textContainerInner = document.createElement('span');
  textContainer.appendChild(textContainerInner);
  document.body.appendChild(textContainer);

  textContainerInner.innerHTML = sanitizeHtml(text.replace(/\n/g, '<br>'));
  const pars = textContainerInner.querySelectorAll('p');
  if (pars) {
    pars.forEach((el, index) => {
      if (pars.length - 1 > index) {
        if (el?.firstChild?.nodeName?.toLowerCase() !== 'br') {
          el.after(document.createElement('br'));
        }
      }
      el.setAttribute('style', 'padding:0; margin:0; display: inline;');
    });
  }

  textContainerInner.setAttribute(
    'style',
    `
      line-height: 1.3;
      font-family: ${fontFamily};
      letter-spacing: 0px;
      vertical-align: top;
    `
  );

  textContainer.setAttribute(
    'style',
    `
      line-height: 1.3;
      font-size: ${fontSize};
      max-width: ${maxWidth};
      box-sizing: content-box;
      position: fixed;
      top: 0; left: 0;
      text-align: ${textAlign};
      font-weight: 500;
      word-break: break-word;
      white-space: pre-wrap;
    `
  );

  const containerRect = textContainer.getBoundingClientRect();
  const innerContainerRect = textContainerInner.getBoundingClientRect();

  return {
    textContainer,
    textContainerInner,
    containerRect,
    innerContainerRect,
    textPadding,
    fontSizeToUse: fontSize,
    textAlignToUse: textAlign,
  };
}

function calculateLineRects(
  textContainerInner: HTMLElement,
  containerTop: number,
  containerLeft: number,
  textPadding: number,
  textAlign: Property.TextAlign
): Rect[] {
  // Do not create rects for line breaks - leave empty space instead
  const nonEmptyParagraphRects = [...textContainerInner.querySelectorAll('p')]
    .filter((p) => !(p.textContent?.trim() === '' && p.querySelector('br')))
    .flatMap((p) => [...p.getClientRects()]);

  const lineRects = nonEmptyParagraphRects.reduce<Rect[]>((lines, rect) => {
    const lastLine = lines[0];
    const rectTop = rect.top - containerTop;
    const rectLeft = rect.left - containerLeft;

    if (lastLine?.top === rectTop) {
      const lineRight = Math.max(
        lastLine.left + lastLine.width - textPadding * 2,
        rectLeft + rect.width
      );
      lastLine.width = lineRight - lastLine.left + textPadding * 2;
      return lines;
    }

    return [
      {
        width: rect.width + textPadding * 2,
        height: rect.height * 1.3,
        top: rectTop,
        left: rectLeft,
      },
      ...lines,
    ];
  }, []);

  if (textAlign === 'right') {
    const maxRightEdge = Math.max(...lineRects.map((r) => r.left + r.width));
    lineRects.forEach((r) => (r.left = maxRightEdge - r.width));
  }

  return lineRects;
}

function adjustRectsForTextAlignment(
  lineRects: Rect[],
  textAlign: Property.TextAlign | undefined,
  containerWidth: number,
  finalCurrentWidth: number,
  originalBoxLeft: number
) {
  let adjustedRects = lineRects;
  let finalBoxLeft = originalBoxLeft;

  if (textAlign && textAlign !== 'left') {
    const widthDifference = containerWidth - finalCurrentWidth;

    if (widthDifference > 0) {
      adjustedRects = lineRects.map((r) => {
        let newLeft = r.left;
        if (textAlign === 'right') {
          newLeft = r.left - widthDifference;
        } else if (textAlign === 'center') {
          newLeft = r.left - widthDifference / 2;
        }
        return {...r, left: newLeft};
      });

      if (textAlign === 'right') {
        finalBoxLeft = originalBoxLeft - widthDifference;
      } else if (textAlign === 'center') {
        finalBoxLeft = originalBoxLeft - widthDifference / 2;
      }
    }
  }
  return {adjustedRects, finalBoxLeft};
}

// eslint-disable-next-line complexity, max-params
export function getRectsWithText(
  text: string,
  style: TextStyles = {},
  mode: BackgroundType = BackgroundTypes.Line,
  textScaleFactor: number,
  // The rects drawn in Guides are slightly too short if we don't pass these args, even though they aren't used?!
  _objectScale: number,
  _canvasWidth: number,
  objectPlacementEnabled: boolean = true,
  isPreviewMode: boolean = false
) {
  const {
    textContainer,
    textContainerInner,
    containerRect,
    innerContainerRect,
    textPadding,
    fontSizeToUse,
    textAlignToUse,
  } = setupTextMeasurementContainers(
    text,
    style,
    textScaleFactor,
    isPreviewMode
  );

  const {
    top: containerTop,
    left: containerLeft,
    height: containerHeight,
    width: containerWidth,
  } = containerRect;

  const {
    top: innerContainerTop,
    left: innerContainerLeft,
    height: innerContainerHeight,
    width: innerContainerWidth,
  } = innerContainerRect;

  let lineRectsFromClientRects: Rect[] = [];
  if (mode !== BackgroundTypes.Transparent) {
    lineRectsFromClientRects = calculateLineRects(
      textContainerInner,
      containerTop,
      containerLeft,
      textPadding,
      textAlignToUse
    );
  }

  const finalCurrentWidth = objectPlacementEnabled
    ? innerContainerWidth + 1
    : containerWidth;
  const currentHeight = containerHeight;

  let adjustedRects = lineRectsFromClientRects;
  const originalBoxLeft = innerContainerLeft - containerLeft;
  let finalBoxLeft = originalBoxLeft;

  if (objectPlacementEnabled) {
    const alignmentResult = adjustRectsForTextAlignment(
      lineRectsFromClientRects,
      textAlignToUse,
      containerWidth,
      finalCurrentWidth,
      originalBoxLeft
    );
    adjustedRects = alignmentResult.adjustedRects;
    finalBoxLeft = alignmentResult.finalBoxLeft;
  }

  textContainer.remove();

  if (mode === BackgroundTypes.Box) {
    return {
      rects: [
        {
          top: innerContainerTop - containerTop,
          left: finalBoxLeft,
          width: innerContainerWidth + textPadding * 2,
          height:
            innerContainerHeight +
            parseInt(fontSizeToUse.replace('px', '')) * 0.3,
          borderRadius: isPreviewMode ? 1 : 2,
        },
      ],
      currentWidth: finalCurrentWidth,
      currentHeight,
    };
  } else {
    return {
      rects: adjustedRects,
      currentWidth: finalCurrentWidth,
      currentHeight,
    };
  }
}

export const sanitizeHtmlWithoutTextAlign = (str: string) => {
  if (!str) {
    return '';
  }

  const clean = DOMPurify.sanitize(str, {USE_PROFILES: {html: true}});

  return clean.replace(
    /style\s*=\s*["']([^"']*?)["']/gi,
    (_match: string, styleContent: string) => {
      const cleanedStyle = styleContent
        .replace(/text-align\s*:\s*[^;]*;?/gi, '') // Remove text-align: value;
        .replace(/;\s*;/g, ';') // Clean up double semicolons
        .replace(/^\s*;\s*/, '') // Remove leading semicolon
        .replace(/;\s*$/, '') // Remove trailing semicolon
        .trim();

      return cleanedStyle ? `style="${cleanedStyle}"` : '';
    }
  );
};
